'use client';

import { languages } from '@/constant/language';
import { AllSubKeysMessageResource } from '@/i18n/messages';
import { cn } from '@/lib/utils';
import { usePathname, useRouter } from '@/navigation';
import { FlagOption, LanguageResponse } from '@/server/api/v1/language/types';
import { Check, Languages } from 'lucide-react';
import { useLocale, useTranslations } from 'next-intl';
import { useSearchParams } from 'next/navigation';
import { PropsWithChildren, useEffect, useState } from 'react';
import CustomImageWithFallback from '../custom-image-with-fallback';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger,
} from '../ui/dropdown-menu';
import { localeLanguages } from './groups-locales-by-primary-language';

const isEnabled = languages.length > 1;

/** Tạm ẩn lá cờ, chỉ hiển thị mã ngôn ngữ (EN, VI, ...). Đổi thành true để hiện lại cờ */
const SHOW_FLAG = false;

/** Lấy mã ngôn ngữ ngắn gọn để hiển thị: 'vi-VN' -> 'VI' */
const getLangCode = (key?: string) =>
    (key ?? '').split(/[-_]/)[0].toUpperCase();

function CustomLanguageSelector({
    className,
    activeLangs = [],
    flags = [],
}: PropsWithChildren<{
    className?: string;
    activeLangs?: LanguageResponse[];
    flags?: FlagOption[];
}>) {
    const locale = useLocale();
    const { replace } = useRouter();
    const pathname = usePathname();
    const searchParams = useSearchParams();
    const transNationalLang = useTranslations('NationalLanguage');

    const [locales, setLocales] = useState<FlagOption[]>([]);
    const [flag, setFlag] = useState<FlagOption>();

    useEffect(() => {
        const _locales =
            activeLangs.map((x) => {
                const langKey = localeLanguages[x.Iso!]?.main_nation;

                return {
                    key: x.Iso ?? '',
                    name: x.AppLanguageResourceCode
                        ? transNationalLang(
                              x.AppLanguageResourceCode as AllSubKeysMessageResource<'NationalLanguage'>
                          )
                        : (x.Name ?? ''),
                    value: langKey
                        ? (flags?.find((f) => f.key === langKey)?.value ?? '')
                        : '',
                };
            }) ?? [];

        setLocales(_locales);
    }, [activeLangs, flags, transNationalLang]);

    useEffect(() => {
        const _data = locales?.find((y) => y.key === locale) ?? undefined;
        setFlag(_data);
    }, [locale, locales]);

    if (!isEnabled || locales.length === 0) {
        return <></>;
    }

    const changeLocale = async (lang?: string) => {
        const str = searchParams.toString();
        replace(pathname + (str.length > 0 ? `?${str}` : ''), {
            locale: lang,
        });
    };

    const renderFlag = (showName: boolean) =>
        flag ? (
            <div className='flex items-center gap-x-2'>
                <div
                    className={cn(
                        'flex w-8 max-w-8',
                        {
                            'h-8 rounded-full': !showName,
                        },
                        className
                    )}
                >
                    <CustomImageWithFallback
                        src={flag.value}
                        fallback={flag.value}
                        alt={flag.name}
                        className={cn('w-full', {
                            'h-full rounded-full object-cover': !showName,
                        })}
                    />
                </div>
                <div hidden={!showName}>{flag.name}</div>
            </div>
        ) : (
            <Languages className='h-4 w-4' />
        );

    /** Hiển thị mã ngôn ngữ đang chọn (EN, VI) thay cho lá cờ */
    const renderCode = (isTrigger: boolean) => {
        if (!flag) {
            return <Languages className='h-4 w-4' />;
        }

        const Comp = isTrigger ? 'button' : 'span';

        return (
            <Comp
                {...(isTrigger && { type: 'button' as const })}
                title={flag.name}
                aria-label={flag.name}
                className={cn(
                    /* Cùng màu với icon thông báo trên header (Button variant='secondary') */
                    'inline-flex h-8 w-8 shrink-0 select-none items-center justify-center rounded-full bg-secondary text-xs font-bold leading-none tracking-wide text-secondary-foreground ring-offset-background transition-colors',
                    {
                        'outline-none hover:bg-secondary/80 focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 data-[state=open]:bg-secondary/80':
                            isTrigger,
                    }
                )}
            >
                {getLangCode(flag.key)}
            </Comp>
        );
    };

    if (locales.length < 2) {
        // Only show the flag if there is only one language
        return SHOW_FLAG ? renderFlag(false) : renderCode(false);
    }

    return (
        <DropdownMenu>
            <DropdownMenuTrigger asChild className='cursor-pointer'>
                {SHOW_FLAG ? renderFlag(false) : renderCode(true)}
            </DropdownMenuTrigger>
            <DropdownMenuContent
                arrowPadding={4}
                align='end'
                className='w-48 p-1'
            >
                <div className='max-h-80 overflow-y-auto'>
                    {locales.map((x, i) => {
                        const isActive = x.key === locale;

                        return (
                            <DropdownMenuItem
                                key={i}
                                onClick={() => changeLocale(x.key)}
                                className={cn(
                                    'flex w-full cursor-pointer items-center justify-start gap-2 whitespace-break-spaces p-2 text-start',
                                    {
                                        'bg-primary/20 font-semibold hover:bg-primary/40':
                                            isActive,
                                        'mb-1': i !== locales.length - 1,
                                    }
                                )}
                            >
                                {SHOW_FLAG ? (
                                    <CustomImageWithFallback
                                        src={x.value}
                                        alt={x.name}
                                        className='h-6 w-7'
                                    />
                                ) : (
                                    <span
                                        className={cn(
                                            'inline-flex h-6 min-w-9 items-center justify-center rounded border px-1.5 text-xs font-semibold tracking-wider',
                                            isActive
                                                ? 'border-primary bg-primary text-primary-foreground'
                                                : 'border-border text-muted-foreground'
                                        )}
                                    >
                                        {getLangCode(x.key)}
                                    </span>
                                )}
                                <span className='flex-1 text-sm'>{x.name}</span>
                                {isActive && (
                                    <Check className='h-4 w-4 text-primary' />
                                )}
                            </DropdownMenuItem>
                        );
                    })}
                </div>
            </DropdownMenuContent>
        </DropdownMenu>
    );
}

export default CustomLanguageSelector;
